import { TruckingGuestRoutePointDto } from "../dtos/ShipmentTruckingGuestInfoResponseDto";
import { downloadBlob } from "../../../../utils/downloadBlob";
import { TargetLocation } from "../dtos/TargetLocation";
import { CheckinState } from "../hooks/useCheckinCheckout";
import { RouteOverview } from "./RouteOverview";
import { useCallback, useMemo, useState } from "react";
import { DownloadOutlined, EyeOutlined, PaperClipOutlined } from "@ant-design/icons";
import { CommonForm, FItem, FormInstance, useCommonForm, useCommonWatch } from "../../../base/components/form/Common";
import { VERTICAL_LAYOUT_ANTD } from "../../constance/VERTICAL_LAYOUT_ANTD";
import { GuestTruckingContSealRequestDto } from "../dtos/GuestTruckingContSealRequestDto";
import { useUpdateContSeal } from "../hooks/useUpdateContSeal";
import { queryClient } from "../../../../utils/QueryClient";
import { TRUCKING_GUEST_INFO_KEY } from "../hooks/useShipmentTruckingGuestInfo";
import { shipmentTruckingService } from "../../services/ShipmentTruckingService";
import type { TruckingFileResponseDto } from "../../dtos/response/TruckingFileResponseDto";
import { useFilePreview } from "../../hooks/useFilePreview";
import type { ShipmentFileResponseDto } from "../../dtos/types/ShipmentFile";
import { CommonInput } from "../../../base/components/data-entry/CommonInput";
import { CommonModal } from "../../../base/components/modal/Common";
import { CommonButton } from "../../../base/components/buttons/CommonButton";
import { CommonMessage } from "../../../base/components/message/CommonMessage";
import { CommonSpin } from "../../../base/components/spin/CommonSpin";
import { CommonDrawer } from "../../../base/components/drawer/CommonDrawer";
import { FilePreview } from "../../../base/components/files/FilePreview";

type DriverDispatchInfoProps = {
  truckingId: string;
  form: FormInstance;
  currentStep: number;
  totalSteps: number;
  geoReady: boolean;
  geoError: string | null;
  targetLocation: TargetLocation | null;
  distanceToTarget: number | null;
  isWithinGeofence: boolean;
  loading: boolean;
  isLast: boolean;
  checkinState: CheckinState;
  checkinTime: string | null;
  onAddressSelected?: (lat: number, lng: number, label: string) => void;
  currentRoutePoint?: TruckingGuestRoutePointDto | null;
  nextRoutePoint?: TruckingGuestRoutePointDto | null;
  checkinRadiusKm: number;
  routePoints: TruckingGuestRoutePointDto[];
  truckingFiles?: TruckingFileResponseDto[];
  vendorName?: string | null;
  onManualCheckin?: () => void;
  onManualCheckout?: () => void;
};

const ManualActionButtons = ({
  checkinState,
  loading,
  geoReady,
  onManualCheckin,
  onManualCheckout,
}: {
  checkinState: CheckinState;
  loading: boolean;
  geoReady: boolean;
  onManualCheckin?: () => void;
  onManualCheckout?: () => void;
}) => {
  const showCheckin = checkinState === "IDLE" && !!onManualCheckin;
  const showCheckout = checkinState === "CHECKED_IN" && !!onManualCheckout;

  if (!showCheckin && !showCheckout) return null;

  return (
    <div className="flex gap-2 mt-2">
      {showCheckin && (
        <button
          onClick={onManualCheckin}
          disabled={loading || !geoReady}
          className={`
            uppercase flex-1 rounded-lg border text-xs font-medium transition-colors duration-150
            ${loading || !geoReady ? "opacity-40 cursor-not-allowed !bg-gray-200 !text-gray-500" : "bg-gradient-to-r from-[#001e5a] to-[#001e5a] text-white hover:text-green-500"}
          `}
          style={{ height: "36px", borderColor: "#001e5a" }}
        >
          {loading ? "Đang xử lý..." : "Check-in"}
        </button>
      )}
      {showCheckout && (
        <button
          onClick={onManualCheckout}
          disabled={loading}
          className={`
            uppercase flex-1 rounded-lg border text-xs font-medium transition-colors duration-150
            ${loading || !geoReady ? "opacity-40 cursor-not-allowed !bg-gray-200 !text-gray-500" : "bg-gradient-to-r from-[#001e5a] to-[#001e5a] text-white hover:text-green-500"}
          `}
          style={{ height: "36px", borderColor: "#001e5a" }}
        >
          {loading ? "Đang xử lý..." : "Check-out"}
        </button>
      )}
    </div>
  );
};

const DistanceRow = ({
  distanceToTarget,
  isWithinGeofence,
  radiusKm,
}: {
  distanceToTarget: number | null;
  isWithinGeofence: boolean;
  radiusKm: number;
}) => {
  const dist = distanceToTarget !== null && !isNaN(distanceToTarget) ? `${distanceToTarget.toFixed(2)} km` : "---";

  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-xs text-gray-500">Khoảng cách đến đích</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium tabular-nums text-gray-800">{dist}</span>
        <span
          className="text-[11px] font-medium px-1.5 py-0.5 rounded border"
          style={
            isWithinGeofence
              ? { background: "#f0fdf4", borderColor: "#bbf7d0", color: "#15803d" }
              : { background: "#fff7ed", borderColor: "#fed7aa", color: "#c2410c" }
          }
        >
          {isWithinGeofence ? `Trong ${radiusKm}km` : `Ngoài ${radiusKm}km`}
        </span>
      </div>
    </div>
  );
};

const CheckinStatusCard = ({
  checkinState,
  checkinTime,
  isWithinGeofence,
  geoReady,
  isLast,
}: {
  checkinState: CheckinState;
  checkinTime: string | null;
  isWithinGeofence: boolean;
  geoReady: boolean;
  isLast: boolean;
}) => {
  if (checkinState === "IDLE") {
    if (!geoReady) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
        <p className="text-xs text-gray-500">Di chuyển vào trong bán kính để tự động check-in</p>
      </div>
    );
  }

  if (checkinState === "CHECKED_IN") {
    const timeStr = checkinTime
      ? new Date(checkinTime).toLocaleTimeString("vi-VN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : null;

    return (
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#bbf7d0" }}>
        <div className="flex items-center justify-between px-3 py-2 border-b" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
          <span className="text-xs font-medium text-green-800">Đã check-in</span>
          {timeStr && <span className="text-[11px] text-green-700 font-mono">{timeStr}</span>}
        </div>
        <div className="px-3 py-2 bg-white">
          <p className="text-xs text-gray-500">
            {isWithinGeofence
              ? isLast
                ? "Rời khỏi bán kính để hoàn tất chuyến đi"
                : "Rời khỏi bán kính để tự động check-out và chuyển điểm"
              : "Đang ghi nhận check-out..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border px-3 py-2.5" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
      <p className="text-xs font-medium text-green-800">Hoàn tất — đang chuyển điểm...</p>
    </div>
  );
};

type DriverFilesDrawerProps = {
  open: boolean;
  truckingId: string;
  files: TruckingFileResponseDto[];
  isFetchingFiles: boolean;
  onClose: () => void;
  onOpen: () => void;
};

const DriverFilesDrawer = ({ open, truckingId, files, isFetchingFiles, onClose, onOpen }: DriverFilesDrawerProps) => {
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const fetchBlobForPreview = useCallback(async (shipmentTruckingId: string, fileId: string) => {
    const data = await shipmentTruckingService.downloadFile(shipmentTruckingId, fileId);
    return { data };
  }, []);

  const { previewFile, blobUrl, loadingPreview, openPreview: openPreviewFile, closePreview } = useFilePreview(fetchBlobForPreview, truckingId);

  const closePreviewAndReturnToList = useCallback(() => {
    closePreview();
    if (files.length) onOpen();
  }, [closePreview, files.length, onOpen]);

  const pviewFile = useCallback(
    (file: TruckingFileResponseDto) => {
      openPreviewFile(file as unknown as ShipmentFileResponseDto);
      onClose();
    },
    [onClose, openPreviewFile],
  );

  const dowloadFile = useCallback(
    async (file: TruckingFileResponseDto) => {
      setDownloadingFileId(file.id);

      try {
        const data = await shipmentTruckingService.downloadFile(truckingId, file.id);
        downloadBlob(data, file.originalFileName, file.contentType || "application/octet-stream");
      } catch (e: any) {
        CommonMessage.error(e?.response?.data?.message ?? "Tải File thất bại");
      } finally {
        setDownloadingFileId(null);
      }
    },
    [truckingId],
  );

  const previewIndex = previewFile ? files.findIndex((file) => file.id === previewFile.id) : -1;

  return (
    <>
      <CommonDrawer open={open} title="CHỨNG TỪ" footer={null} onClose={onClose} width={560}>
        {isFetchingFiles && !files.length ? (
          <div className="flex justify-center py-8">
            <CommonSpin />
          </div>
        ) : files.length ? (
          <div className="flex flex-col gap-2">
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <PaperClipOutlined className="text-blue-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-800" title={file.originalFileName}>
                    {file.originalFileName}
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-400">{[file.fileSize, file.registeredTime].filter(Boolean).join(" · ")}</div>
                </div>
                <CommonButton size="small" icon={<EyeOutlined />} onClick={() => pviewFile(file)} />
                <CommonButton
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloadingFileId === file.id}
                  onClick={() => dowloadFile(file)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-gray-400">Không có chứng từ</div>
        )}
      </CommonDrawer>

      <FilePreview
        file={previewFile as any}
        blobUrl={blobUrl}
        loading={loadingPreview}
        onClose={closePreviewAndReturnToList}
        onDownload={(file) => dowloadFile(file as unknown as TruckingFileResponseDto)}
        hasPrev={previewIndex > 0}
        hasNext={previewIndex >= 0 && previewIndex < files.length - 1}
        onPrev={() => previewIndex > 0 && pviewFile(files[previewIndex - 1])}
        onNext={() => previewIndex >= 0 && previewIndex < files.length - 1 && pviewFile(files[previewIndex + 1])}
      />
    </>
  );
};

export const DriverDispatchInfo = ({
  truckingId,
  form,
  currentStep,
  totalSteps,
  geoReady,
  geoError,
  targetLocation,
  distanceToTarget,
  isWithinGeofence,
  loading,
  isLast,
  checkinState,
  checkinTime,
  onAddressSelected: _onAddressSelected,
  currentRoutePoint,
  checkinRadiusKm,
  routePoints,
  truckingFiles,
  onManualCheckin,
  onManualCheckout,
}: DriverDispatchInfoProps) => {
  const [isUpdateContSeal, setIsUpdateContSeal] = useState<boolean>(false);
  const [isViewPaper, setIsViewPaper] = useState<boolean>(false);
  const { mutate: updateContSeal } = useUpdateContSeal();
  const formUpdateContSeal = useCommonForm<GuestTruckingContSealRequestDto>();
  const files = truckingFiles ?? [];
  const isFetchingFiles = false;
  const hasFiles = files.length > 0;
  const watchedContainerNo = useCommonWatch<string | null | undefined>("containerNo", form);
  const containerNo = watchedContainerNo ?? form.getFieldValue("containerNo");
  const hasContainerNo = typeof containerNo === "string" ? containerNo.trim().length > 0 : Boolean(containerNo);

  const saveUpdateContSeal = useCallback(
    (values: GuestTruckingContSealRequestDto) => {
      updateContSeal(
        {
          shipmentTruckingId: truckingId,
          payload: values,
        },
        {
          onSuccess: () => {
            CommonMessage.success("CẬP NHẬT CONT/SEAL THÀNH CÔNG");
            form.setFieldsValue(values);
            formUpdateContSeal.resetFields();
            setIsUpdateContSeal(false);

            queryClient.invalidateQueries({
              queryKey: [TRUCKING_GUEST_INFO_KEY, truckingId],
            });
          },
          onError: (e: any) => {
            CommonMessage.error(e?.response?.data?.result ?? e?.response?.data?.message);
          },
        },
      );
    },
    [truckingId, form, formUpdateContSeal, updateContSeal],
  );

  const canceUpdateContSeal = useCallback(() => {
    formUpdateContSeal.resetFields();
    setIsUpdateContSeal(false);
  }, [formUpdateContSeal]);

  const TRIP_INFO_FIELDS = useMemo(
    () =>
      [
        { name: "identity", label: "MÃ VẬN CHUYỂN" },
        { name: "containerNo", label: "SỐ CONT" },
        { name: "sealNo", label: "SỐ SEAL" },
        { name: "shipmentBill", label: "LIST BOOKI/BILL" },
        { name: "driverName", label: "TÊN TÀI XẾ" },
        { name: "driverPhone", label: "SĐT TÀI XẾ" },
        { name: "licensePlate", label: "BIỂN SỐ XE" },
      ] as const,
    [],
  );

  const TripInfo = useMemo(
    () => (
      <CommonForm form={form} component={false}>
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
          {TRIP_INFO_FIELDS.map(({ name, label }) => (
            <div key={name} className="flex flex-col sm:flex-row sm:items-center px-3 py-2 gap-1 sm:gap-3">
              <span className="text-[11px] text-gray-400 sm:w-40 flex-shrink-0 uppercase tracking-wide">{label}</span>

              <FItem name={name} noStyle>
                <CommonInput variant="borderless" readOnly className="!px-0 text-xs text-gray-700 flex-1 truncate" />
              </FItem>
            </div>
          ))}
        </div>
      </CommonForm>
    ),
    [form, TRIP_INFO_FIELDS],
  );

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <div className="min-w-0">
            {form.getFieldValue("vendorName") && <p className="text-[11px] text-gray-400 truncate mb-0.5">{form.getFieldValue("vendorName")}</p>}
            <h2 className="text-sm font-semibold text-gray-800 leading-tight">{currentRoutePoint?.stopTypeName ?? "Đang tải..."}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              ĐỊA ĐIỂM {currentStep + 1} / {totalSteps}
              {checkinRadiusKm && <span className="ml-2 text-gray-300">· BÁN KÍNH {checkinRadiusKm}KM</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center mt-4">
          {!loading && !hasContainerNo && (
            <CommonButton
              onClick={() => setIsUpdateContSeal(true)}
              className="!bg-gradient-to-r from-[#001e5a] to-[#001e5a] !text-white hover:!text-green-500"
            >
              CẬP NHẬT CONT/SEAL
            </CommonButton>
          )}

          {hasFiles && (
            <CommonButton
              onClick={() => setIsViewPaper(true)}
              className="!bg-gradient-to-r from-[#001e5a] to-[#001e5a] !text-white hover:!text-green-500"
            >
              XEM CHỨNG TỪ
            </CommonButton>
          )}

          <CommonModal open={isUpdateContSeal} closable={false} onCancel={canceUpdateContSeal} onOk={() => formUpdateContSeal.submit()}>
            <CommonForm<GuestTruckingContSealRequestDto>
              form={formUpdateContSeal}
              layout={VERTICAL_LAYOUT_ANTD}
              className="space-y-2"
              onFinish={saveUpdateContSeal}
            >
              <FItem<GuestTruckingContSealRequestDto> name="containerNo" label="SỐ CONT" rules={[{ required: true, message: "không được để trống" }]}>
                <CommonInput />
              </FItem>
              <FItem<GuestTruckingContSealRequestDto> name="sealNo" label="SỐ SEAL">
                <CommonInput />
              </FItem>
            </CommonForm>
          </CommonModal>
          <DriverFilesDrawer
            open={isViewPaper}
            truckingId={truckingId}
            files={files}
            isFetchingFiles={isFetchingFiles}
            onClose={() => setIsViewPaper(false)}
            onOpen={() => setIsViewPaper(true)}
          />
        </div>
        {geoError && (
          <div className="mt-2 px-3 py-2 rounded border border-red-100 bg-red-50">
            <p className="text-xs text-red-600">{geoError}</p>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {TripInfo}
        <RouteOverview routePoints={routePoints} currentRoutePoint={currentRoutePoint} accentColor="#374151" />

        {targetLocation && (
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <DistanceRow distanceToTarget={distanceToTarget} isWithinGeofence={isWithinGeofence} radiusKm={checkinRadiusKm} />
          </div>
        )}

        <CheckinStatusCard
          checkinState={checkinState}
          checkinTime={checkinTime}
          isWithinGeofence={isWithinGeofence}
          geoReady={geoReady}
          isLast={isLast}
        />
      </div>
      <div className="px-3 py-3 border-t border-gray-100 bg-white">
        <ManualActionButtons
          checkinState={checkinState}
          loading={loading}
          geoReady={geoReady}
          onManualCheckin={onManualCheckin}
          onManualCheckout={onManualCheckout}
        />
      </div>
    </div>
  );
};
